import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'Why does the Triangle Score look so bad for ETFs?',
    a: 'Because the math doesn\'t apply. The Triangle blends three growth legs: revenue, EPS, and dividend. An ETF doesn\'t have revenue or earnings per share of its own — it\'s a pool of other companies\' shares. So two of the three legs are empty by design, and averaging them in produces a misleadingly low score. SCHD has one of the cleanest distribution histories in the market and shows about 25/100 on the Triangle. That number isn\'t wrong; it\'s just answering a question that doesn\'t fit ETFs.',
  },
  {
    q: 'What should I look at instead?',
    a: 'Five things, all surfaced in the Stock Modal for any ETF: (1) expense ratio (under 0.20% is excellent), (2) net assets / AUM (above $1B is stable), (3) distribution growth on the Div Growth tab, (4) holdings concentration via the ETF Profile, (5) the fund family and category. Together these answer the real question — "will this ETF still pay growing distributions five years from now" — much better than the Triangle ever could.',
  },
  {
    q: 'Does DiviDrip handle ETF judgement automatically?',
    a: 'Partly. The Triangle panel now labels ETF scores as "Distribution-only score · use Div Growth + Fund Profile" instead of the alarming "High risk of cut" verdict that applies to single-name stocks. The ETF Profile section of the Stock Modal then shows expense ratio, AUM, fund family, and holdings — exactly the inputs you need.',
  },
  {
    q: 'Should I just ignore the Triangle Score on ETFs?',
    a: 'Mostly yes. The score still reflects whether the FUND\'s distribution stream has grown over time — which is useful, just narrow. For full evaluation use the Div Growth tab and the Fund Profile fields. The Triangle is great for single-name stocks; it\'s a partial picture for ETFs.',
  },
];

export default function LearnEvaluatingDividendEtfs() {
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  return (
    <LearnPage
      title="How to Evaluate Dividend ETFs — The Triangle Score Doesn't Apply (Here's What Does) | DiviDrip"
      description="ETFs have no underlying revenue or EPS of their own, so the Dividend Triangle math doesn't fit. The 5 metrics that actually matter for ETFs — expense ratio, AUM, distribution growth, holdings concentration, fund family — and how to read them in DiviDrip."
      canonical="https://twylightcrow.com/learn/evaluating-dividend-etfs"
      guidePath="/learn/evaluating-dividend-etfs"
      sectionLabel="Metrics & Evaluation"
      sectionAnchor="metrics"
      kicker="Learn · ETF evaluation"
      h1="How to Evaluate Dividend ETFs — What to Use When the Triangle Doesn't Apply"
      datePublished="2026-05-24"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/dividend-triangle', label: 'Reading the Dividend Triangle (single-name stocks)' },
        { to: '/learn/jepi-vs-jepq', label: 'JEPI vs JEPQ — head-to-head' },
        { to: '/learn/schd-vs-voo', label: 'SCHD vs VOO — yield vs total return' },
        { to: '/learn/is-9-percent-yield-safe', label: 'Is a 9%+ yield safe?' },
        { to: '/learn/covered-call-etfs', label: 'Covered-call ETFs explained' },
      ]}
    >
      <p>
        Open SCHD on DiviDrip. The Triangle Score reads about <strong>25 out of
        100</strong>, in a worrying shade of red. Look just below it and you see
        SCHD&apos;s actual record: 5-year distribution growth of nearly{' '}
        <strong>+40%</strong>, a 9.13% 5-year dividend CAGR, expense ratio of{' '}
        <strong>0.06%</strong>, $91 billion in AUM, and one of the cleanest
        institutional pedigrees in the industry. So what gives?
      </p>
      <p>
        The Triangle isn&apos;t broken — it&apos;s just answering the wrong
        question for ETFs. Let&apos;s look at why, and what you should actually
        check.
      </p>

      <h2>Why the Triangle is a one-legged stool for ETFs</h2>
      <p>
        The <Link to="/learn/dividend-triangle">Dividend Triangle</Link> bundles
        three growth signals into a single score:
      </p>
      <ol>
        <li><strong>Revenue growth</strong> — the company&apos;s top-line CAGR.</li>
        <li><strong>EPS growth</strong> — earnings per share CAGR.</li>
        <li><strong>Dividend growth</strong> — payout CAGR.</li>
      </ol>
      <p>
        For a single-name stock like JNJ, KO, or PG, all three exist and tell
        you whether the underlying business is healthy. For an ETF, two of those
        three legs are <strong>structurally empty</strong>. SCHD doesn&apos;t
        have revenue — it&apos;s a basket of 100+ companies. It doesn&apos;t
        have EPS — it&apos;s a fund, not an operating business. The only leg
        that exists is the <strong>distribution leg</strong> — the
        fund&apos;s actual payout history to shareholders.
      </p>
      <p>
        Average those three legs together and you get a misleadingly low
        composite. DiviDrip now flags this directly: the Triangle Score for any
        ETF is labeled <em>&quot;Distribution-only score · use Div Growth + Fund
        Profile&quot;</em> in neutral blue, replacing the &quot;High risk of
        cut&quot; verdict that fires for low-scoring single-name stocks. The
        score itself is preserved (it&apos;s still a real measurement of the
        distribution leg), but the framing matches reality.
      </p>

      <h2>The 5 metrics that DO answer the question for ETFs</h2>

      <h3>1. Expense Ratio (lower is better)</h3>
      <p>
        What the fund charges you annually as a percentage of assets. Compounds
        directly against your return.
      </p>
      <table className="learn-table">
        <thead>
          <tr><th>Range</th><th>Verdict</th><th>Examples</th></tr>
        </thead>
        <tbody>
          <tr><td>Under 0.10%</td><td>Excellent — institutional-grade pricing</td><td>SCHD 0.06%, VOO 0.03%, VYM 0.06%</td></tr>
          <tr><td>0.10% – 0.30%</td><td>Reasonable for specialized strategies</td><td>DGRO 0.08%, JEPI 0.35%</td></tr>
          <tr><td>0.30% – 0.75%</td><td>Acceptable for actively-managed or niche ETFs</td><td>JEPQ 0.35%, NOBL 0.35%</td></tr>
          <tr><td>Over 0.75%</td><td>Expensive — you&apos;d better see the value</td><td>PFFA 1.55% (leveraged), some thematic ETFs</td></tr>
        </tbody>
      </table>
      <p>
        A 0.50% expense ratio doesn&apos;t sound like much until you compound
        it: over 30 years, that fee compounds to roughly{' '}
        <strong>14% of your total return</strong> compared to a 0.06% fund.
        Pay attention here. The expense ratio is visible on the Stock
        Modal&apos;s Dividend Snapshot for every ETF.
      </p>

      <h3>2. Net Assets / AUM (Assets Under Management)</h3>
      <p>
        How much money the fund manages. Bigger funds are more liquid (tighter
        spreads), less likely to close, and signal institutional confidence.
      </p>
      <table className="learn-table">
        <thead>
          <tr><th>AUM</th><th>Verdict</th></tr>
        </thead>
        <tbody>
          <tr><td>Over $10B</td><td>Blue-chip ETF. Massive liquidity, near-zero closure risk. SCHD ($91B), VOO, VTI.</td></tr>
          <tr><td>$1B – $10B</td><td>Established. Plenty of liquidity, very low closure risk. JEPI, JEPQ, DIVO.</td></tr>
          <tr><td>$100M – $1B</td><td>Niche but viable. Check fund-family commitment.</td></tr>
          <tr><td>Under $100M</td><td>Closure risk. If AUM doesn&apos;t grow, the issuer may shut the fund — forcing a sale at potentially unfavorable timing.</td></tr>
        </tbody>
      </table>

      <h3>3. Distribution Growth (the Div Growth tab)</h3>
      <p>
        The single most important metric for an income-focused ETF. Are the
        payouts growing, flat, or shrinking? DiviDrip&apos;s{' '}
        <strong>Div Growth tab</strong> on the Stock Modal shows:
      </p>
      <ul>
        <li><strong>5-year growth %</strong> — total distribution change over 5 years</li>
        <li><strong>5-year CAGR</strong> — annualized growth rate</li>
        <li><strong>3-year CAGR</strong> — shorter-horizon growth</li>
        <li><strong>Current annual rate</strong> — what you&apos;ll receive on $1 of investment</li>
      </ul>
      <p>
        SCHD&apos;s numbers (5yr +39.7%, 5yr CAGR 8.7%) tell the real story the
        Triangle can&apos;t. JEPI&apos;s look different (yield is high but
        growth has been roughly flat — covered-call income is current-yield-
        focused, not growth-focused). Both are sustainable. Both pass the right
        test for the right purpose.
      </p>

      <h3>4. Holdings Concentration (the ETF Profile section)</h3>
      <p>
        How many positions does the ETF hold, and is the top 10 a concentrated
        bet or a diversified basket?
      </p>
      <ul>
        <li><strong>SCHD</strong> holds about 100 stocks with the top 10 representing ~40% of AUM. Concentrated by design — quality-focused dividend payers.</li>
        <li><strong>VYM</strong> holds 400+ stocks with the top 10 at ~25%. Maximum diversification.</li>
        <li><strong>QYLD</strong> holds 100 NASDAQ-100 names with the top 10 (AAPL/MSFT/NVDA-heavy) at ~50%. Tech-concentrated by structure.</li>
      </ul>
      <p>
        DiviDrip&apos;s Stock Modal shows the holdings list and sector
        breakdown on the <strong>ETF Profile</strong> tab. A 99% stocks /
        ~0% bonds split (like SCHD) tells you the fund will move with equity
        markets. A 60/40 stocks/bonds blend tells you something different.
      </p>

      <h3>5. Fund Family + Category</h3>
      <p>
        Who manages the fund matters. The big institutional families have lower
        fees, deeper resources, and longer track records:
      </p>
      <ul>
        <li><strong>Schwab</strong> — SCHD, SCHV, SCHY. Ultra-low-cost, broad mandates.</li>
        <li><strong>Vanguard</strong> — VOO, VYM, VIG. Industry-defining cost discipline.</li>
        <li><strong>iShares (BlackRock)</strong> — DGRO, HDV, DVY. Broadest lineup, very low fees.</li>
        <li><strong>JPMorgan</strong> — JEPI, JEPQ. Actively managed equity-linked income.</li>
        <li><strong>Global X</strong> — QYLD, XYLD, DIVO. Specialized income strategies.</li>
        <li><strong>State Street SPDR</strong> — SPY, SPYI, SPYD. Original ETF family.</li>
      </ul>
      <p>
        Category (Large Value, Large Blend, etc.) tells you what segment the
        ETF targets. SCHD is &quot;Large Value&quot; — quality dividend payers.
        JEPI is &quot;Derivative Income&quot; — covered-call premium plus
        equity exposure. Knowing the category sets expectations for what the
        fund will and won&apos;t do.
      </p>

      <h2>Quick-look — six popular dividend ETFs side by side</h2>
      <table className="learn-table">
        <thead>
          <tr><th>ETF</th><th>Yield</th><th>Expense</th><th>AUM</th><th>5yr Div Growth</th></tr>
        </thead>
        <tbody>
          <tr><td>SCHD</td><td>~3.3%</td><td>0.06%</td><td>$91B</td><td>+39.7%</td></tr>
          <tr><td>VYM</td><td>~2.8%</td><td>0.06%</td><td>$54B</td><td>+22%</td></tr>
          <tr><td>DGRO</td><td>~2.3%</td><td>0.08%</td><td>$28B</td><td>+38%</td></tr>
          <tr><td>JEPI</td><td>~9.5%</td><td>0.35%</td><td>$32B</td><td>~flat (premium-driven)</td></tr>
          <tr><td>JEPQ</td><td>~10.4%</td><td>0.35%</td><td>$22B</td><td>~flat (premium-driven)</td></tr>
          <tr><td>QYLD</td><td>~11.9%</td><td>0.61%</td><td>$8B</td><td>declining (NAV decay)</td></tr>
        </tbody>
      </table>
      <p>
        Reading just yields tells you JEPI / JEPQ / QYLD &quot;win&quot;. Reading the
        whole row tells the actual story — SCHD and DGRO compound, JEPI and
        JEPQ generate yield without compounding, QYLD pays the highest yield
        but loses NAV over time. The right ETF depends on what you&apos;re
        optimizing for.
      </p>

      <h2>The 30-second ETF evaluation workflow</h2>
      <ol>
        <li>Open the ticker on <Link to="/dashboard">DiviDrip</Link>.</li>
        <li>Glance at the Triangle — confirm it shows the &quot;Distribution-only score&quot; label, then move on. Don&apos;t panic about the number.</li>
        <li>Check expense ratio (Dividend Snapshot). Under 0.20%? Great. Over 0.50%? Be sure you know why.</li>
        <li>Check AUM. Over $1B? Stable. Under $100M? Closure risk.</li>
        <li>Switch to the Div Growth tab. Is the 5-year CAGR positive? Quality income ETF. Flat? Probably a yield-focused covered-call fund. Negative? Walk away unless you understand the structure.</li>
        <li>Open the ETF Profile. Skim the top 10 holdings and sector breakdown. Does the concentration make sense for what you want?</li>
      </ol>
      <p>
        Six steps, sixty seconds. You now know more about the ETF than 90% of
        retail buyers — and you weren&apos;t mislead by a Triangle Score
        designed for a different kind of security.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Bottom line</h2>
      <p>
        The Triangle Score is one of the strongest tools on DiviDrip for
        evaluating single-name dividend stocks. It&apos;s just not the right
        tool for ETFs, and the new labeling makes that explicit. For ETFs,
        the answer lives in expense ratio + AUM + Div Growth tab + ETF Profile
        — five fields that together answer the right question: <em>&quot;Will
        this fund still pay growing distributions in 2031?&quot;</em>.
      </p>
    </LearnPage>
  );
}
